import { type Canary, GFE_CANARY, NATIVE_API_CANARY, canaryHeaderName, toVariant } from '@/logic/experiments/canaries';
import { COOKIES } from '@/utils/cookies/constants';
import type { AppDataSource, AppRouteKind } from './app-data.types';

/**
 * One structured line per page render recording which content API actually served that user.
 *
 * Datadog ingests the pod's stdout, so a single-line JSON object using Datadog's reserved
 * attributes (`status`, `usr.id`) is all that is required — `loggerMiddleware` emits its
 * per-request log the same way. Everything else is namespaced under `app_data.` so the rollout can
 * be sliced per user without colliding with other attributes.
 *
 * `app_data.outcome` is the field to group and alert on: it carries the whole story in one value,
 * so no dashboard needs to correlate `source` against `canary_variant` by hand. `source` and
 * `canary_variant` are still logged as the raw facts behind it.
 *
 * `gfe_canary_variant` is the GFE rollout variant, logged alongside rather than folded into
 * `outcome`. GFE bypasses `resolveAppData` for everything it serves, but the layout still resolves
 * app data for the shell, so `gfe` here means "this user was rolled out to GFE", not "GFE served
 * this". Pair it with `(getCategoryChromeSource) source=` to see whether GFE actually answered.
 *
 * `next/headers` is imported dynamically, as in `getCanaryVariant`. `resolveAppData` — the only
 * caller — is reachable from client component graphs, and a static import would put `next/headers`
 * in the static module graph and fail the build with "only works in a Server Component".
 */

/** Only the `get` this module needs, so it does not depend on `next/headers` types either. */
type HeaderReader = { get: (name: string) => string | null };

export type AppDataFallback = {
    /**
     * `native-failed` is something breaking — the native request or its adaptation. `route-disabled`
     * is not a failure but a routing mismatch: the middleware assigned native for the request path
     * while the render resolved a route outside the native set. Kept apart so an alert on breakage
     * cannot fire on a route that was simply never in the rollout.
     */
    kind: 'native-failed' | 'route-disabled';
    reason: string;
};

/**
 * Every state a render can end in, as one closed set. Logged-out is a value rather than a separate
 * flag because the middleware stamps control before it bails on logged-out requests, so without it
 * `page-config` would mean both "assigned control" and "was never eligible".
 */
export type AppDataOutcome =
    /** Assigned native, and native served the page. */
    | 'native-served'
    /** Assigned native, native broke, page-config served the page instead. */
    | 'native-fallback'
    /** Assigned native, but this route is not native-api enabled. Indicates a route mismatch. */
    | 'native-route-disabled'
    /** Assigned a non-native arm, served by page-config as intended. */
    | 'page-config'
    /** Logged out, so the canary never applied. Not part of any arm. */
    | 'logged-out';

export type AppDataSourceLog = {
    route: string;
    routeKind: AppRouteKind;
    /** What actually produced the data the page rendered. */
    source: AppDataSource;
    /** Why native did not serve. Omitted when it did, or when the user was never on native. */
    fallback?: AppDataFallback;
};

const OUTCOME_MESSAGES: Record<AppDataOutcome, string> = {
    'native-served': 'app data served from native api',
    'native-fallback': 'app data fell back to page config after native api failed',
    'native-route-disabled': 'app data used page config: route is not native-api enabled',
    'page-config': 'app data served from page config',
    'logged-out': 'app data served from page config: logged out, canary not eligible'
};

/** A fallback and a route mismatch are both worth alerting on; a normal render is not. */
const WARN_OUTCOMES = new Set<AppDataOutcome>(['native-fallback', 'native-route-disabled']);

/**
 * The arm the middleware stamped. `unstamped` covers both a route the middleware skipped and a
 * value the canary does not declare, so a client-supplied header cannot fake an assignment.
 */
const getStampedVariant = <TVariant extends string>(
    headerList: HeaderReader,
    canary: Canary<TVariant>
): TVariant | 'unstamped' => toVariant(canary, headerList.get(canaryHeaderName(canary))) ?? 'unstamped';

/**
 * Any real involvement with native — served, broken, or route-disabled — outranks the eligibility
 * label, so `logged-out` only ever means "page-config by default, never in an arm". Today logged-out
 * requests cannot reach native at all (the middleware stamps control before bailing on them), but if
 * logged-out traffic is ever moved onto native, this must report `native-served` rather than hide it
 * behind `logged-out`. `app_data.auth` carries the auth dimension either way.
 */
const getOutcome = (
    source: AppDataSource,
    fallback: AppDataFallback | undefined,
    isLoggedIn: boolean
): AppDataOutcome => {
    if (source === 'native-api') {
        return 'native-served';
    }
    if (fallback?.kind === 'native-failed') {
        return 'native-fallback';
    }
    if (fallback?.kind === 'route-disabled') {
        return 'native-route-disabled';
    }
    if (!isLoggedIn) {
        return 'logged-out';
    }

    return 'page-config';
};

export const logAppDataSource = async ({ route, routeKind, source, fallback }: AppDataSourceLog): Promise<void> => {
    try {
        const { cookies, headers } = await import('next/headers');
        const [headerList, cookieStore] = await Promise.all([headers(), cookies()]);

        // No `x-pathname` means the middleware never ran for this request — its matcher excludes
        // images and other assets — so this is a 404 or asset response re-rendering the layout
        // rather than a page view. Logging it would invent `page-config` traffic no user ever saw.
        if (!headerList.get('x-pathname')) {
            return;
        }

        // Read the same cookies `canaryMiddleware` checks, so the two cannot disagree about who was
        // eligible for an assignment.
        const isLoggedIn = Boolean(cookieStore.get(COOKIES.AUTH_TOKEN)?.value);
        const outcome = getOutcome(source, fallback, isLoggedIn);

        const userId = cookieStore.get(COOKIES.USER_ID)?.value;
        // The identity Amplitude bucketed on, so a Datadog line can be joined to its assignment.
        const deviceId = cookieStore.get(COOKIES.AMP_DEVICE_ID)?.value;

        const entry: Record<string, unknown> = {
            time: new Date().toISOString(),
            status: WARN_OUTCOMES.has(outcome) ? 'warn' : 'info',
            message: OUTCOME_MESSAGES[outcome],
            route,
            'app_data.outcome': outcome,
            'app_data.source': source,
            'app_data.canary_variant': getStampedVariant(headerList, NATIVE_API_CANARY),
            'app_data.gfe_canary_variant': getStampedVariant(headerList, GFE_CANARY),
            'app_data.route_kind': routeKind,
            'app_data.auth': isLoggedIn ? 'logged-in' : 'logged-out'
        };

        if (fallback) {
            entry['app_data.fallback_reason'] = fallback.reason;
        }
        if (userId) {
            entry['usr.id'] = userId;
        }
        if (deviceId) {
            entry['amp.device_id'] = deviceId;
        }

        process.stdout.write(`${JSON.stringify(entry)}\n`);
    } catch {
        // Observability must never break a render.
    }
};
